import { Button, Card, CardContent, Checkbox, Divider, FormControlLabel, Grid, Link, TextField, Tooltip, Typography } from '@mui/material';
import { getMoveToInvoicedForms, moveFormsToInvoiced, MoveToInvoicedFormDto } from 'api';
import { Page } from 'components';
import { Formik } from 'formik';
import { useSnackbar } from 'notistack';
import { useRef, useState } from 'react';
import { useDebounce } from 'utils';
import * as yup from 'yup';

interface MoveToInvoicedFormValues {
  searchFormNumber: string;
  selectedFormIds: number[];
  invoiceNumber: string;
  overwrite: boolean;
}

const moveToInvoicedInit: MoveToInvoicedFormValues = {
  searchFormNumber: '',
  selectedFormIds: [],
  invoiceNumber: '',
  overwrite: false
};

const moveToInvoicedValidationSchema = yup.object<MoveToInvoicedFormValues>({
  invoiceNumber: yup
    .string()
    .max(20, 'Invoice # cannot be more than 20 characters.')
    .nullable(),
  selectedFormIds: yup.array().of(yup.number().integer().positive())
});

export default function MoveToInvoiced() {
  const { enqueueSnackbar } = useSnackbar();
  const previousValueRef = useRef<string>('');
  const [forms, setForms] = useState<MoveToInvoicedFormDto[]>([]);
  const [isSearching, setIsSearching] = useState<boolean>(false);
  const [isMoving, setIsMoving] = useState<boolean>(false);

  const debouncedSearch = useDebounce((searchText: string) => {
    const trimmed = searchText.trim();

    // Only search when job # length is present (6 digits). When shorter, clear results.
    if (trimmed.length < 6) {
      setForms([]);
      return;
    }

    setIsSearching(true);
    getMoveToInvoicedForms(trimmed)
      .then(({ value }) => {
        setForms(value ?? []);
      })
      .catch(() => {
        setForms([]);
      })
      .finally(() => {
        setIsSearching(false);
      });
  }, 300);

  const handleFormNumberChange = (value: string, setFieldValue: (field: string, value: any) => void) => {
    // Remove all non-digit and non-dash characters
    let cleaned = value.replace(/[^\d-]/g, '');

    // Check if user is backspacing at the dash position (7th character)
    const previousValue = previousValueRef.current;
    if (previousValue && previousValue.length > cleaned.length) {
      // User is deleting characters
      const previousDigits = previousValue.split('-')[0];
      const currentDigits = cleaned.split('-')[0];
      
      // If previous had exactly 6 digits + dash and current has exactly 6 digits (dash was removed), remove the 6th digit too
      if (
        previousDigits.length === 6 &&
        previousValue.includes('-') &&
        previousValue.length === 7 &&
        currentDigits.length === 6 &&
        !cleaned.includes('-')
      ) {
        cleaned = currentDigits.substring(0, 5);
      }
    }

    // Enforce pattern: ^\d{6}-\d+$
    // Allow up to 6 digits
    const digitsBeforeDash = cleaned.split('-')[0];
    if (digitsBeforeDash.length > 6) {
      cleaned = digitsBeforeDash.substring(0, 6) + (cleaned.includes('-') ? '-' + cleaned.split('-').slice(1).join('-') : '');
    }

    // Only allow dash after exactly 6 digits
    if (cleaned.includes('-')) {
      const parts = cleaned.split('-');
      if (parts[0].length < 6) {
        // If we have a dash but less than 6 digits before it, remove the dash
        cleaned = parts[0];
      } else if (parts.length > 2) {
        // Only allow one dash
        cleaned = parts[0] + '-' + parts.slice(1).join('');
      }
    }

    // Automatically insert dash after 6 digits if not already present
    if (!cleaned.includes('-') && digitsBeforeDash.length === 6) {
      cleaned = digitsBeforeDash + '-';
    }

    // After dash, only allow digits
    if (cleaned.includes('-')) {
      const parts = cleaned.split('-');
      const afterDash = parts[1]?.replace(/\D/g, '') || '';
      cleaned = parts[0] + '-' + afterDash;
    }

    previousValueRef.current = cleaned;
    setFieldValue('searchFormNumber', cleaned);
    // Any search change should clear selection to avoid mismatched ids.
    setFieldValue('selectedFormIds', []);

    if (digitsBeforeDash.length < 6) {
      setForms([]);
      return;
    }

    debouncedSearch(cleaned);
  };

  return (
    <Page title='Move To Invoiced' isLoading={isSearching || isMoving}>
      <Formik
        initialValues={moveToInvoicedInit}
        onSubmit={async (values, formikHelpers) => {
          const selectedForms = forms.filter(form => values.selectedFormIds.includes(form.formId));

          // Build confirmation message
          let confirmMessage = 'You are about to move the following forms to Invoiced:\n';
          confirmMessage += selectedForms.map(f => `- ${f.formNumber}`).join('\n');
          confirmMessage += '\n\nInvoice #: ' + (values.invoiceNumber || '(empty)');
          confirmMessage += '\n\n';
          if (values.overwrite) {
            confirmMessage += 'Existing Invoice # values on these forms will be replaced.';
          } else {
            confirmMessage += 'Existing Invoice # values on these forms will be preserved.';
          }

          if (!window.confirm(confirmMessage)) {
            formikHelpers.setSubmitting(false);
            return;
          }

          setIsMoving(true);
          try {
            const response = await moveFormsToInvoiced({
              forms: selectedForms,
              invoiceNumber: values.invoiceNumber?.trim() ? values.invoiceNumber.trim() : undefined,
              overwriteInvoiceNumber: values.overwrite
            });

            const fallbackMessage = `${selectedForms.length} Forms Moved To Invoicing Successfully!`;
            enqueueSnackbar(response.message || fallbackMessage, { variant: 'success' });

            // Reset to initial state after successful post.
            setForms([]);
            previousValueRef.current = '';
            formikHelpers.resetForm({ values: moveToInvoicedInit });
          } finally {
            setIsMoving(false);
            formikHelpers.setSubmitting(false);
          }
        }}
        validateOnBlur
        validateOnChange
        validationSchema={moveToInvoicedValidationSchema}
      >
        {({ errors, handleBlur, handleChange, handleSubmit, setFieldValue, touched, values }) => {
          // Sync ref with current value if it's different (e.g., on initial load or external change)
          if (previousValueRef.current !== values.searchFormNumber) {
            previousValueRef.current = values.searchFormNumber;
          }

          return (
            <form onSubmit={handleSubmit}>
              <Grid container spacing={2}>
                <Grid item xs={12}>
                  <Card>
                    <CardContent>
                      <Grid container spacing={2}>
                        <Grid item xs={12}>
                          <Typography variant='body1'>
                            This feature allows you to manually move forms from <strong>Submitted</strong> or <strong>Processed</strong> to{' '}
                            <strong>Invoiced</strong>, without requiring an invoice to be created.
                          </Typography>
                        </Grid>
                        <Grid item xs={12} sm={6}>
                          <TextField
                            fullWidth
                            helperText='Searches by Form # (e.g. 123456-1) or Job # (e.g. 123456)'
                            label='Search Form #'
                            size='small'
                            value={values.searchFormNumber}
                            onChange={e => handleFormNumberChange(e.target.value, setFieldValue)}
                          />
                        </Grid>

                        <Grid item xs={12}>
                          <Grid container spacing={1} alignItems='center'>
                            <Grid item>
                              <Typography variant='body1'>
                                {forms.length} form{forms.length !== 1 ? 's' : ''} found
                                {values.selectedFormIds.length > 0 && (
                                  <Typography component='span' variant='body1' sx={{ color: 'error.main', fontWeight: 'semibold', ml: 1 }}>
                                    {values.selectedFormIds.length} form{values.selectedFormIds.length !== 1 ? 's' : ''} selected
                                  </Typography>
                                )}
                                {forms.length > 0 && (
                                  <Link
                                    component='button'
                                    type='button'
                                    underline='always'
                                    sx={{ ml: 1, verticalAlign: 'baseline' }}
                                    onClick={() => {
                                      const allSelected = values.selectedFormIds.length === forms.length;
                                      setFieldValue('selectedFormIds', allSelected ? [] : forms.map(f => f.formId));
                                    }}
                                  >
                                    {values.selectedFormIds.length === forms.length ? 'Deselect All' : 'Select All'}
                                  </Link>
                                )}
                              </Typography>
                            </Grid>
                          </Grid>
                        </Grid>

                        <Grid item xs={12} container spacing={1}>
                          {forms.map(form => (
                            <Grid item xs={12} key={form.formId}>
                              <FormControlLabel
                                control={
                                  <Checkbox
                                    checked={values.selectedFormIds.includes(form.formId)}
                                    onChange={() => {
                                      const newSelectedFormIds = values.selectedFormIds.includes(form.formId)
                                        ? values.selectedFormIds.filter(id => id !== form.formId)
                                        : [...values.selectedFormIds, form.formId];
                                      setFieldValue('selectedFormIds', newSelectedFormIds);
                                    }}
                                  />
                                }
                                label={form.formNumber + ' - ' + form.formStatus}
                              />
                            </Grid>
                          ))}
                        </Grid>

                        <Grid item xs={12}>
                          <Divider />
                        </Grid>

                        <Grid item xs={12} sm={6}>
                          <TextField
                            error={Boolean(touched.invoiceNumber && errors.invoiceNumber)}
                            fullWidth
                            helperText={
                              touched.invoiceNumber && errors.invoiceNumber
                                ? errors.invoiceNumber
                                : values.overwrite
                                  ? 'Existing Invoice # values will be replaced.'
                                  : 'Existing Invoice # values will be preserved.'
                            }
                            label='Invoice #'
                            name='invoiceNumber'
                            onBlur={handleBlur}
                            onChange={handleChange}
                            size='small'
                            value={values.invoiceNumber}
                          />
                        </Grid>

                        <Grid item xs={12} sm={6} sx={{ pl: 3 }}>
                          <Tooltip
                            arrow
                            title='Replaces any existing Invoice # on the selected forms with this Invoice #.'
                          >
                            <FormControlLabel
                              control={
                                <Checkbox
                                  checked={values.overwrite}
                                  name='overwrite'
                                  onChange={handleChange}
                                />
                              }
                              label='Overwrite Form Invoice #'
                            />
                          </Tooltip>
                        </Grid>

                        <Grid item xs={12} container justifyContent='flex-end'>
                          <Button
                            color='primary'
                            disabled={values.selectedFormIds.length === 0}
                            type='submit'
                            variant='contained'
                          >
                            MOVE SELECTED TO INVOICED
                          </Button>
                        </Grid>
                      </Grid>
                    </CardContent>
                  </Card>
                </Grid>
              </Grid>
            </form>
          );
        }}
      </Formik>
    </Page>
  );
}
